<script>
import ScrollToTop from "@core/components/scroll-to-top/ScrollToTop.vue"
import { $themeConfig } from "@themeConfig"
import UpdateAvailableModal from "@/components/UpdateAvailableModal.vue"
import useAutoLogout from "@/composables/useAutoLogout"
import useStore from "@/composables/useStore"
import AnnouncementService from "@/services/info/announcement.service"

export default {
  name: "App",
  components: {
    ScrollToTop,
    UpdateAvailableModal,
    // Vue 3: Use defineAsyncComponent for async component imports
    LayoutVertical: defineAsyncComponent(
      () => import("@core/layouts/layout-vertical/LayoutVertical.vue"),
    ),
    LayoutHorizontal: defineAsyncComponent(
      () => import("@core/layouts/layout-horizontal/LayoutHorizontal.vue"),
    ),
    LayoutFull: defineAsyncComponent(
      () => import("@core/layouts/layout-full/LayoutFull.vue"),
    ),
    AppCustomizer: defineAsyncComponent(
      () => import("@core/layouts/components/app-customizer/AppCustomizer.vue"),
    ),
  },
  setup() {
    const instance = getCurrentInstance()
    const { proxy } = instance

    // Store composable
    const {
      skin,
      layoutType,
      announcements,
      updateWindowWidth,
      setAnnouncements,
    } = useStore()

    useAutoLogout(() => proxy.$logout())

    // Reactive state
    const enableScrollToTop = ref(
      $themeConfig?.layout?.enableScrollToTop ?? true,
    )
    const showCustomizer = ref($themeConfig?.layout?.customizer ?? false)
    const announcementTimer = ref(null)

    // Computed properties
    const skinClasses = computed(() => {
      const skinValue = skin.value
      if (skinValue === "bordered")
        return "bordered-layout"
      if (skinValue === "semi-dark")
        return "semi-dark-layout"
      if (skinValue === "dark")
        return "backImagedark"
      if (skinValue === "light")
        return "backImage"
      return null
    })

    const layout = computed(() => {
      const route = proxy.$route
      if (route.meta.layout === "full")
        return "LayoutFull"
      return layoutType.value === "horizontal"
        ? "LayoutHorizontal"
        : "LayoutVertical"
    })

    const showAnnouncements = computed(() => {
      return proxy.$route.path !== "/login" && announcements.value.length > 0
    })

    // Methods
    const handleWindowResize = () => {
      updateWindowWidth(window.innerWidth)
    }

    const initializeTheme = () => {
      // Set direction
      const isRTL = $themeConfig?.layout?.isRTL
      document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr")

      // Apply dark mode
      const skinValue = skin.value
      if (skinValue === "dark") {
        document.body.classList.add("dark-layout")
      } else {
        document.body.classList.remove("dark-layout")
      }
    }

    let scheduleNextAnnouncementFetch
    const fetchAnnouncements = async () => {
      const accessToken = localStorage.getItem("access_token")
      if (!accessToken)
        return

      try {
        const response = await AnnouncementService.GetNew()
        if (response.status === 200 && response.data) {
          localStorage.setItem("announcements", JSON.stringify(response.data))
          setAnnouncements(response.data)
        }
      } catch (err) {
        console.warn("Failed to fetch announcements:", err)
      } finally {
        scheduleNextAnnouncementFetch()
      }
    }

    scheduleNextAnnouncementFetch = () => {
      if (announcementTimer.value) {
        clearTimeout(announcementTimer.value)
      }
      announcementTimer.value = setTimeout(() => fetchAnnouncements(), 30000)
    }

    const startAnnouncementPolling = () => {
      fetchAnnouncements()
    }

    const stopAnnouncementPolling = () => {
      if (announcementTimer.value) {
        clearTimeout(announcementTimer.value)
        announcementTimer.value = null
      }
    }

    // Lifecycle hooks
    onMounted(() => {
      initializeTheme()
      startAnnouncementPolling()
      handleWindowResize()
      window.addEventListener("resize", handleWindowResize)
    })

    onBeforeUnmount(() => {
      window.removeEventListener("resize", handleWindowResize)
      stopAnnouncementPolling()
    })

    return {
      enableScrollToTop,
      showCustomizer,
      skinClasses,
      layout,
      announcements,
      showAnnouncements,
    }
  },
}
</script>

<template>
  <div
    id="app"
    class="h-100"
    :class="[
      skinClasses,
      { 'has-announcement': showAnnouncements },
    ]"
  >
    <component :is="layout">
      <RouterView />

      <template #customizer>
        <AppCustomizer v-if="showCustomizer" />
      </template>
    </component>

    <UpdateAvailableModal />

    <!-- Scroll to Top Button -->
    <ScrollToTop v-if="enableScrollToTop" />

    <!-- Announcements Banner -->
    <div
      v-if="showAnnouncements"
      class="announcement-banner"
    >
      <div class="announcement-banner__content">
        <span class="announcement-banner__text">
          <span class="announcement-banner__track">
            <svg
              class="announcement-banner__icon"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path
                d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"
              />
              <line
                x1="12"
                y1="9"
                x2="12"
                y2="13"
              />
              <line
                x1="12"
                y1="17"
                x2="12.01"
                y2="17"
              />
            </svg>
            <span
              v-for="(item, index) in announcements"
              :key="index"
            >{{ item
            }}<span
              v-if="index < announcements.length - 1"
              class="announcement-banner__separator"
            >
              |
            </span></span>
          </span>
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Announcement Banner - Slim top bar */
.announcement-banner {
  position: fixed;
  z-index: 9999;
  top: 0;
  left: 0;
  right: 0;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  background-color: #fef3cd;
  border-bottom: 1px solid #ffc107;
  color: #856404;
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
}

.announcement-banner__content {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.announcement-banner__icon {
  width: 16px;
  height: 16px;
  margin-right: 8px;
  vertical-align: -3px;
  color: #d97706;
}

.announcement-banner__text {
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
}

.announcement-banner__track {
  display: inline-block;
  padding-left: 100%;
  animation: announcement-marquee 25s linear infinite;
}

.announcement-banner__text:hover .announcement-banner__track {
  animation-play-state: paused;
}

@keyframes announcement-marquee {
  to {
    transform: translateX(-100%);
  }
}

.announcement-banner__separator {
  color: #c29a1f;
}
</style>

<style>
/* Push layout down when announcement banner is visible */
.has-announcement .vertical-layout {
  height: calc(100vh - 32px);
  margin-top: 32px;
}

/* Global styles */
.backImage {
  background-image: url("/xalqbanki.png");
  background-size: 100% 100%;
  background-attachment: fixed;
}

/* Vue Select overrides */
.vs--single .vs__selected {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  max-width: calc(100% - 20px);
}

.vs--single .vs__selected-options {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  max-width: 100%;
}
</style>
